import type { ReferenceCard, ReferenceLibraryMeta } from '@scoutaround/script-breakdown-shared';

import type { IconName } from '../Icon.js';

/**
 * THE LIBRARY'S SMALL WORDS: sizes, ages, what a reference is drawn as, and the
 * upload limit said in a sentence. Everything numeric about an upload comes from
 * `GET references/meta` (owner decision R2) — nothing here knows a limit.
 */

/** 31457280 → "30 MB", 12400000 → "11.8 MB"; one decimal below a hundred. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${String(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 100 || Number.isInteger(value) ? Math.round(value) : Math.round(value * 10) / 10;
  return `${String(rounded)} ${units[unit] ?? 'TB'}`;
}

/** "jpg", ".JPG" and "Jpg" are one extension. */
export function normaliseExtension(extension: string): string {
  return extension.trim().replace(/^\./, '').toLowerCase();
}

export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot <= 0 || dot === filename.length - 1 ? '' : normaliseExtension(filename.slice(dot + 1));
}

/** The upload limit as the drawer and the drop zone say it: "Up to 30 MB per file · JPG, PNG, PDF". */
export function uploadLimitText(upload: ReferenceLibraryMeta['upload']): string {
  const types = upload.acceptedExtensions.map((e) => normaliseExtension(e).toUpperCase()).filter((e) => e !== '');
  const size = `Up to ${formatBytes(upload.maxBytes)} per file`;
  return types.length === 0 ? size : `${size} · ${types.join(', ')}`;
}

/** The `accept` attribute for the file picker, from the same list. */
export function acceptAttribute(upload: ReferenceLibraryMeta['upload']): string {
  return upload.acceptedExtensions
    .map((e) => normaliseExtension(e))
    .filter((e) => e !== '')
    .map((e) => `.${e}`)
    .join(',');
}

/**
 * Why a picked file will not be sent, or null when it may be. Checked in the
 * browser so a 2 GB video is refused before it starts, not after a minute.
 */
export function refuseFile(file: File, upload: ReferenceLibraryMeta['upload']): string | null {
  const accepted = new Set(upload.acceptedExtensions.map(normaliseExtension));
  const extension = extensionOf(file.name);
  if (accepted.size > 0 && !accepted.has(extension)) {
    return extension === ''
      ? 'This file has no type the library accepts.'
      : `.${extension} files cannot be added here.`;
  }
  if (file.size === 0) return 'This file is empty.';
  if (file.size > upload.maxBytes) {
    return `Larger than the ${formatBytes(upload.maxBytes)} limit (${formatBytes(file.size)}).`;
  }
  return null;
}

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi', 'mpg', 'mpeg', 'ogv']);

export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/') || VIDEO_EXTENSIONS.has(extensionOf(file.name));
}

/** What a card shows when there is no re-encoded preview to show. */
export function referenceIcon(card: Pick<ReferenceCard, 'sourceType' | 'file'>): IconName {
  if (card.sourceType === 'NOTE') return 'note';
  if (card.sourceType === 'LINK') return 'link';
  if (card.sourceType === 'LOCATION') return 'pin';
  switch (card.file?.kind) {
    case 'IMAGE':
      return 'image';
    case 'VIDEO':
      return 'video';
    case 'PDF':
      return 'pdf';
    default:
      return 'file';
  }
}

/** "PDF", "Video", "Image" — the file's kind in a word, for the card's meta line. */
export function fileKindLabel(kind: NonNullable<ReferenceCard['file']>['kind']): string {
  switch (kind) {
    case 'IMAGE':
      return 'Image';
    case 'VIDEO':
      return 'Video';
    case 'PDF':
      return 'PDF';
    case 'DOCUMENT':
      return 'Document';
    default:
      return 'File';
  }
}

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "3 hours ago", "yesterday" — with the exact time in `absoluteTime` for a tooltip. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const seconds = Math.round((then.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return 'just now';
  if (abs < 45 * 60) return RELATIVE.format(Math.round(seconds / 60), 'minute');
  if (abs < 22 * 3600) return RELATIVE.format(Math.round(seconds / 3600), 'hour');
  if (abs < 26 * 86400) return RELATIVE.format(Math.round(seconds / 86400), 'day');
  if (abs < 320 * 86400) return RELATIVE.format(Math.round(seconds / (30 * 86400)), 'month');
  return RELATIVE.format(Math.round(seconds / (365 * 86400)), 'year');
}

/** The display format the workspace uses everywhere: d.m.Y H:i:s. */
export function absoluteTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** "Laura Mendes" → "LM"; a person the directory could not name reads "Member". */
export function personLabel(displayName: string | null): string {
  return displayName ?? 'Member';
}

export function initials(displayName: string | null): string {
  if (displayName === null) return '·';
  const parts = displayName.trim().split(/\s+/).filter((p) => p !== '');
  const letters = parts.length === 1 ? (parts[0] ?? '').slice(0, 2) : `${parts[0]?.[0] ?? ''}${parts.at(-1)?.[0] ?? ''}`;
  return letters.toUpperCase() || '·';
}

/** A pasted string that is worth asking the server about: a host with a dot, scheme optional. */
export function looksLikeUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '' || /\s/.test(trimmed)) return false;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
}

/** The link as the card shows it when the server named no domain. */
export function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
